import { api } from './client'

export type LinhaDeRoteamento = {
  motivo: string

  /** Frase para quem opera — "DegradacaoDaIa" não diz nada a quem lê. */
  rotulo: string
  time: string

  /** Verdadeiro quando não há parâmetro gravado: o valor mostrado é o recuo do código. */
  padrao: boolean
}

export type Roteamento = {
  assistenteId: number | null
  times: string[]
  linhas: LinhaDeRoteamento[]
  timesConfigurados: boolean
}

const raiz = (marca: string) => `/marcas/${encodeURIComponent(marca)}/roteamento`

export const roteamento = {
  ler: (marca: string, assistenteId?: number) =>
    api.get<Roteamento>(`${raiz(marca)}${assistenteId ? `?assistenteId=${assistenteId}` : ''}`),

  salvar: (
    marca: string,
    assistenteId: number | null,
    times: string[],
    destinos: Record<string, string>,
  ) => api.put<{ alteradas: number }>(raiz(marca), { assistenteId, times, destinos }),
}

/**
 * O resumo redigido sobre os fatos (demanda 041).
 *
 * 🔴 Os três estados são diferentes e a tela precisa distinguir: **texto**
 * (redação pronta), **semBase** (não há histórico para resumir) e
 * **indisponivel** (a IA não respondeu — os fatos continuam, o resumo não vem).
 * Colapsar os dois últimos em "sem resumo" faria o atendente concluir "cliente
 * novo" de uma indisponibilidade.
 */
export type ResumoDoContato = {
  texto: string | null
  geradoEm: string | null
  indisponivel: boolean
  semBase: boolean
}

export type MensagemNoTurno = { papel: string; texto: string }

export type TurnoDoAssistente = {
  existe: boolean
  em: string | null
  assistente: string | null
  modelo: string | null
  versaoDoPrompt: number | null

  /** O prompt mudou depois daquele turno — a resposta estranha pode ser de outra versão. */
  promptMudouDepois: boolean

  contexto: MensagemNoTurno[]
}

export type ContextoDaConversa = {
  contato: {
    id: number
    nome: string | null
    criadoEm: string
    identificadores: { tipo: string; valor: string; canal: string | null; ultimoVistoEm: string }[]
  }
  totalDeConversas: number
  conversas: {
    id: number
    idExterno: number
    canal: string
    marca: string
    iniciadaEm: string
    ultimaAtividadeEm: string
    pausada: boolean
    motivoDaPausa: string | null
    mensagens: number

    /**
     * Por que a conversa está pausada, quando veio de um transbordo (demanda
     * 124). Nulo é "não sei" — pausa da trava anti-laço, ou anterior à demanda
     * — e **não** pode ser lido como pedido do cliente.
     */
    motivoDoTransbordoDaPausa: string | null

    /**
     * As etiquetas do ciclo desta conversa (demanda 167) — `orçamento enviado`,
     * `follow-up de orçamento`, o freio.
     *
     * Chegam **deduplicadas pelo back**, que compara pela mesma regra do freio:
     * enquanto a 159 não entrar, a mesma etiqueta existe escrita de dois jeitos.
     * O front não repete essa regra — conhecimento em cópia privada é
     * conhecimento esperando para ser reaprendido.
     */
    etiquetas: string[] | null
  }[]
  orcamentos: {
    /**
     * A referência da PLANILHA (com a letra de reedição, demanda 276) — o número
     * que a equipe e o cliente conhecem. `null` quando o orçamento não tem
     * referência (livro, linha sem correspondente no GE).
     *
     * 🔴 **O número interno (`2026-NNNN`) não vem mais** (demanda 292, conversa
     * 552): era ele que o painel mostrava. O back deixou de mandá-lo, de modo que
     * a tela não tem como voltar a exibi-lo.
     */
    referencia: string | null
    familia: string
    tiragem: number
    total: number
    unitario: number
    calculadoEm: string
    origem: string
  }[]
  transbordos: { motivo: string; time: string; em: string; efetivado: boolean }[]
  enviados: { tipo: string; resumo: string; criadoEm: string }[]

  /**
   * A IA está atendendo ESTA conversa? (ficha 310). Nulo quando o painel foi aberto sem a
   * conversa, ou quando o portal ainda não a registrou — não há o que afirmar.
   */
  ia?: EstadoDaIa | null
}

/**
 * Por que a IA não responde — os nomes são os do enum do back, e não são livres. A ordem é a
 * dos freios no servidor; a tela recebe só o primeiro.
 */
export type MotivoDaIaCalada =
  | 'CaixaDesligada'
  | 'EtiquetaNaConversa'
  | 'NumeroDaCasa'
  | 'EtiquetaNoContato'
  | 'PausadaPorTransferencia'
  | 'PausadaPorTrava'
  | 'SilencioPorFalaHumana'
  | 'TravaDeLaco'
  | 'SemAssistente'

/**
 * O estado da IA numa conversa (ficha 310).
 *
 * 🔴 **Decidido no servidor, pela mesma regra que cala a IA quando a mensagem chega.** O
 * front não refaz a conta: escreve a frase e converte a hora.
 */
export type EstadoDaIa = {
  atendendo: boolean
  motivo: MotivoDaIaCalada | null

  /** Quando o freio começou, se o servidor sabe: a fala da pessoa, a pausa. UTC. */
  desde: string | null

  /** Quando a IA volta sozinha — só o silêncio por fala humana tem fim. UTC. */
  ate: string | null

  /** O silêncio vem de OUTRA conversa do mesmo contato: o número dela no Chatwoot. */
  deOutraConversa: number | null

  /** O que o servidor sabe dizer do motivo: a causa da transferência, a trava. */
  detalhe: string | null

  /** O time ou a pessoa a quem a conversa foi passada. */
  time: string | null

  /** Há botão para ESTA pessoa: só no silêncio, e só para quem pode liberar. */
  podeLiberar: boolean

  /**
   * Há o botão "Pausar a IA" para ESTA pessoa (ficha 317): só com a IA atendendo, com o
   * silêncio depois de fala humana ligado no servidor, e para quem pode.
   *
   * ⚠️ Opcional, como os três abaixo: o servidor anterior à 317 não os manda, e a tela tem de
   * funcionar contra ele — sem o campo, não há botão nem linha.
   */
  podePausar?: boolean

  /** Por quantas horas a pausa cala a IA — a janela do silêncio no servidor. */
  horasDaPausa?: number | null

  /** O nome de quem clicou em "Liberar a IA", enquanto a IA atende por causa desse clique. */
  liberadaPor?: string | null

  /** Quando foi o clique. UTC. */
  liberadaEm?: string | null
}

/**
 * Contexto do contato (`P-38`).
 *
 * Devolve 204 quando o contato ainda não foi reconhecido — o que é diferente de
 * erro. `null` aqui significa "ainda não sei quem é", e a tela precisa dizer
 * isso, não mostrar falha.
 */
/** Uma entrada da linha do tempo unificada (demanda 042). */
export type ItemDaLinhaDoTempo = {
  /** `Acervo` (histórico migrado) ou `Portal` (conversa do sistema novo). */
  origem: 'Acervo' | 'Portal'

  /** Protocolo, no acervo; id da conversa, no portal. */
  referencia: number

  em: string
  canal: string | null

  /** A primeira fala do cliente, ou o departamento. Nunca vazio. */
  assunto: string

  /**
   * Endereço externo do item — hoje só a conversa do portal, na plataforma de
   * atendimento. O item do acervo vem sem link **de propósito**: ele abre aqui
   * mesmo, porque nova aba não funcionaria (o cookie é `Partitioned` e o
   * contato chega por `postMessage` — nenhum dos dois sai do iframe).
   */
  link: string | null
}

export type LinhaDoTempoDoContato = {
  total: number
  primeira: string | null
  ultima: string | null
  canais: string[]

  /** 🔴 A partir de quando existe histórico — sem isto, ausência vira "cliente novo". */
  cobertura: string | null

  /** Verdadeiro quando o canal do contato não casa com o acervo (Instagram). */
  canalNaoCasa: boolean

  itens: ItemDaLinhaDoTempo[]
}

export const contexto = {
  buscar: async (identificador?: string, conversaExterna?: number) => {
    const parametros = new URLSearchParams()
    if (identificador) parametros.set('identificador', identificador)
    if (conversaExterna) parametros.set('conversaExterna', String(conversaExterna))

    return api.get<ContextoDaConversa | null>(`/contexto?${parametros}`)
  },

  linhaDoTempo: async (identificador?: string, conversaExterna?: number) => {
    const parametros = new URLSearchParams()
    if (identificador) parametros.set('identificador', identificador)
    if (conversaExterna) parametros.set('conversaExterna', String(conversaExterna))

    return api.get<LinhaDoTempoDoContato | null>(`/contexto/linha-do-tempo?${parametros}`)
  },

  /**
   * O resumo do contato — em rota separada de propósito.
   *
   * Os fatos renderizam primeiro; o resumo chega depois. Segurar a tela pela
   * redação faria o painel parecer lento justamente na abertura, que é quando
   * ele é lido.
   */
  resumo: async (identificador?: string, conversaExterna?: number) =>
    api.get<ResumoDoContato | null>(`/contexto/resumo?${Parametros(identificador, conversaExterna)}`),

  /**
   * **"Liberar a IA"** (ficha 310): encerra o silêncio aberto pela fala de uma pessoa, nesta
   * conversa. Não dispara resposta — a IA volta na próxima mensagem do cliente.
   *
   * 409 quando o que cala a IA não é o silêncio; o corpo traz `detail` (o gesto que serve) e
   * `ia` (o estado de agora).
   */
  liberarIa: async (conversaExterna: number) =>
    api.post<{ ia: EstadoDaIa; aviso: string }>('/contexto/ia/liberar', { conversaExterna }),

  /**
   * **"Pausar a IA"** (ficha 317): abre agora o silêncio desta conversa, como se uma pessoa da
   * equipe tivesse respondido. A IA para de responder pelo tempo da janela do servidor.
   *
   * 409 quando não há o que pausar (a conversa já calou por outro caminho) ou quando o silêncio
   * está desligado no servidor; o corpo traz `detail` e `ia` (o estado de agora).
   */
  pausarIa: async (conversaExterna: number) =>
    api.post<{ ia: EstadoDaIa }>('/contexto/ia/pausar', { conversaExterna }),

  /** Buscado só quando alguém abre o bloco: mostra a conversa inteira. */
  ultimoTurno: async (identificador?: string, conversaExterna?: number) =>
    api.get<TurnoDoAssistente | null>(
      `/contexto/ultimo-turno?${Parametros(identificador, conversaExterna)}`),
}

function Parametros(identificador?: string, conversaExterna?: number) {
  const parametros = new URLSearchParams()

  if (identificador) parametros.set('identificador', identificador)
  if (conversaExterna) parametros.set('conversaExterna', String(conversaExterna))

  return parametros.toString()
}
